/* Where this tenant's metadata is sent, as three states rather than two.
 *
 * The API is deliberate about this and the screen has to be too: `_route_json` returns
 * `configured: false` with a note rather than an empty route object, because "nobody set this up"
 * and "somebody switched it off" are different facts and a screen rendering both as blank fields
 * would make the second look like the first. A *disabled* route is `configured: true,
 * enabled: false`, and `core.ai.anomaly` reports it as `TEMPLATE_ONLY` — by configuration — where an
 * *unreachable* one is `UNAVAILABLE`. That distinction cost a defect once already: a disabled route
 * was read as our outage, so an admin closing a maintenance window had every anomalous finding
 * reported as a platform failure. Three states here is what keeps the screen on the right side of it.
 */

import type { ServingBackend, ServingRouteView } from '@/shared/api/generated/ai-serving';

/** Not a boolean, for the reason in this file's opening paragraph. */
export type RouteState = 'unconfigured' | 'switched_off' | 'live';

export function routeStateOf(view: ServingRouteView): RouteState {
  if (!view.configured) return 'unconfigured';
  /* `=== false` rather than `!view.enabled`: the field is optional on the unconfigured shape, and
   * `undefined` there must not read as switched off. A configured route the server sent without an
   * `enabled` field is a contract divergence, and reporting it as live — which the API's own default
   * is — is the honest reading rather than inventing a third meaning for an absent field. */
  if (view.enabled === false) return 'switched_off';
  return 'live';
}

/** A total map, so a fifth `RouteState` is a compile error in the file whose subject is the states. */
export function stateKey(state: RouteState): string {
  return `serving.state.${state}`;
}

export function backendKey(backend: ServingBackend): string {
  return `serving.backend.${backend}`;
}

/**
 * Whether the form's current pair would be refused by the platform.
 *
 * **This is a courtesy, not the control.** `ServingRoute.__post_init__` refuses a hosted backend for
 * an air-gapped tenant, `ServingService.configure` answers 422, and a CHECK constraint refuses the
 * row — three layers, none of them this function. It exists so an admin learns why before spending a
 * round trip, and the server's refusal is still rendered when it comes. Saying otherwise would be the
 * claim-without-mechanism defect this codebase records most often.
 *
 * **All three backends stay on offer, and an earlier version filtered them — which was worse in a
 * way only a browser showed.** Removing `hosted` from the option list made the browser reset the
 * displayed value to the first remaining option while React state still held `hosted`, so the screen
 * showed `llama.cpp` selected *beside* a warning telling the reader to choose a backend inside their
 * network: a warning about a selection the page had stopped displaying. That is the internal
 * contradiction §9 records at the profiler's drift panel, reached by a different route.
 *
 * Showing the refused choice and saying why is also the better answer on the merits. Silently
 * changing what somebody selected is the wrong move on a data-egress decision, and the warning names
 * all three enforcing layers, so a reader learns the constraint rather than watching an option
 * vanish.
 */
export function isRefusedPair(backend: ServingBackend, airGapped: boolean): boolean {
  return airGapped && backend === 'hosted';
}
